import { useMemo, useState } from 'react'

import { createNextState } from '@reduxjs/toolkit'

import { RPCClient } from '@buffer-mono/legacy-rpc-utils'
import { Dialog } from '@buffer-mono/popcorn'

import type { Tag as TagType } from '~publish/gql/graphql'

import { PostItemList } from './PostItemList'
import { PostItemsFooter } from './PostItemsFooter'

import styles from './PostItems.module.css'

type Post = {
  profile_ids: string[]
  text?: string
  media?: { photo?: string }
  tags: TagType[]
  created_source: 'bulk_upload'
}

export type ListItem = {
  id: string
  status: 'valid' | 'invalid' | 'loading' | 'success' | 'error'
  errors?: string[]
  warnings?: string[]
  data: Post
}

type CreatePostResponse = {
  success: boolean
  message?: string
  code?: number
}

function isCreatePostResponse(obj: unknown): obj is CreatePostResponse {
  if (
    typeof obj === 'object' &&
    obj !== null &&
    'success' in obj &&
    typeof obj.success === 'boolean'
  ) {
    return true
  }
  return false
}

const rpc = new RPCClient({
  url: '/rpc',
  sendCredentials: 'same-origin',
})

const createPost = async (post: Post): Promise<CreatePostResponse> => {
  const response = await rpc.call('composerApiProxy', {
    url: '/1/updates/create.json',
    args: post,
    HTTPMethod: 'POST',
  })
  if (isCreatePostResponse(response)) {
    return response
  }
  throw new Error('Invalid response from createPost')
}

const updateItemStatus = (
  items: ListItem[],
  id: string,
  status: ListItem['status'],
  errors?: string[],
): ListItem[] => {
  return createNextState(items, (draft) => {
    const item = draft.find((item) => item.id === id)
    if (item) {
      item.status = status
      if (errors) item.errors = errors
    }
    return draft
  })
}

export const PostItems = ({
  items,
  setItems,
  channelId,
  channelName,
  onDialogClose,
}: {
  items: ListItem[]
  setItems: React.Dispatch<React.SetStateAction<ListItem[]>>
  channelId: string
  channelName: string
  onDialogClose: (closeAction: 'success' | 'cancel') => void
}): JSX.Element => {
  const [creationAttempted, setCreationAttempted] = useState(false)
  const [progress, setProgress] = useState(0)

  const [selectedSaveAction, setSelectedSaveAction] = useState<
    'post' | 'draft'
  >('post')

  const validItems = useMemo(
    () => items.filter((item) => item.status === 'valid'),
    [items],
  )
  const validItemCount = validItems.length

  const invalidItems = useMemo(
    () => items.filter((item) => item.status === 'invalid'),
    [items],
  )

  const canStartPostCreation = useMemo(
    () => !creationAttempted && validItemCount > 0,
    [creationAttempted, validItemCount],
  )
  const isCreatingPosts = progress > 0
  const isPostCreationComplete = useMemo(
    () =>
      creationAttempted &&
      items.every(
        (item) =>
          item.status !== 'loading' &&
          item.status !== 'valid' &&
          (item.status === 'error' || item.status === 'success'),
      ),
    [creationAttempted, items],
  )

  const onCreatePosts = async (type: 'post' | 'draft'): Promise<void> => {
    // Set whether items are being saved as drafts or posts
    setSelectedSaveAction(type)

    // Set all invalid items to error status
    invalidItems.forEach((item) => {
      setItems((items) => updateItemStatus(items, item.id, 'error'))
    })

    const total = validItems.length
    for (let i = 0; i < validItems.length; i++) {
      const item = validItems[i]
      try {
        const post = {
          ...item.data,
          is_draft: type === 'draft',
        }
        const response = await createPost(post)
        if (!response.success) {
          throw new Error(response.message)
        }
        setItems((items) => updateItemStatus(items, item.id, 'success'))
      } catch (error) {
        setItems((items) =>
          updateItemStatus(items, item.id, 'error', [
            error instanceof Error ? error.message : 'Unknown error',
          ]),
        )
      }
      setProgress(((i + 1) / total) * 100)
    }

    setProgress(0)

    // Set creation attempted to true
    setCreationAttempted(true)
  }

  const successItemCount = useMemo(
    () => items.filter((item) => item.status === 'success').length,
    [items],
  )
  const title = useMemo(() => {
    if (isPostCreationComplete) {
      if (successItemCount === 0) {
        return `Failed to create ${
          selectedSaveAction === 'draft' ? 'drafts' : 'posts'
        }`
      }
      return selectedSaveAction === 'draft'
        ? 'Saving Complete!'
        : 'Scheduling Complete!'
    }
    if (isCreatingPosts) {
      return selectedSaveAction === 'draft'
        ? 'Saving posts as drafts...'
        : 'Adding posts to queue...'
    }
    return 'Review Content'
  }, [
    isPostCreationComplete,
    isCreatingPosts,
    successItemCount,
    selectedSaveAction,
  ])

  return (
    <>
      <Dialog.Body className={styles.body}>
        <Dialog.Title size="large">{title}</Dialog.Title>
        <PostItemList items={items} />
      </Dialog.Body>
      <PostItemsFooter
        channelId={channelId}
        channelName={channelName}
        items={items}
        selectedSaveAction={selectedSaveAction}
        canStartPostCreation={canStartPostCreation}
        isCreatingPosts={isCreatingPosts}
        isPostCreationComplete={isPostCreationComplete}
        onCreatePosts={(postType) => {
          onCreatePosts(postType).catch(() => null)
        }}
        onDialogClose={onDialogClose}
      />
    </>
  )
}
